// Live access changes - decided 9 October 2026: when someone changes a user's access, the pages that user already
// has open re-read their permissions in place. Never a reload, and never at the cost of unsaved work (see
// accessHold.js and utils/unsavedWork.js).
//
// The server sends an `access-changed` event on the user's notification stream (AccessChangeSignal); the stream
// hook re-dispatches it as ACCESS_CHANGED_EVENT, and useAccessRefresh answers it.

import { LEVEL_RANK } from '../utils/permissionLevel.js';

/** Window event: "this user's access may have changed - re-read it". Detail: the server's signal, if any. */
export const ACCESS_CHANGED_EVENT = 'gp-access-changed';

/** Window event: "a request was refused unexpectedly - check whether access changed under us". */
export const ACCESS_CHECK_EVENT = 'gp-access-check';

/** A tab hidden at least this long re-checks on its return: a signal sent while it was hidden is not replayed. */
export const RECHECK_AFTER_HIDDEN_MS = 30_000;

const rank = (level) => LEVEL_RANK[level] ?? 0;

/**
 * How the effective permissions changed, from the user's point of view:
 *  - 'none'    - nothing that decides access (the version is the server's fingerprint of exactly that);
 *  - 'gained'  - something opened up, nothing closed;
 *  - 'reduced' - something closed (possibly with something else opening up).
 * A page or component missing from a side resolves to that side's default level, as everywhere else.
 */
export const compareAccess = (before, after) => {
    if (!before || !after) return after ? 'gained' : 'none';
    if (before.version && after.version && before.version === after.version) return 'none';
    let gained = false;
    let reduced = false;
    const compareMaps = (a = {}, b = {}) => {
        const keys = new Set([...Object.keys(a || {}), ...Object.keys(b || {})]);
        keys.forEach((k) => {
            const from = rank((a || {})[k] ?? before.defaultPermissionLevel);
            const to = rank((b || {})[k] ?? after.defaultPermissionLevel);
            if (to > from) gained = true;
            if (to < from) reduced = true;
        });
    };
    compareMaps(before.pagePermissions, after.pagePermissions);
    compareMaps(before.componentPermissions, after.componentPermissions);
    const defaultFrom = rank(before.defaultPermissionLevel);
    const defaultTo = rank(after.defaultPermissionLevel);
    if (defaultTo > defaultFrom) gained = true;
    if (defaultTo < defaultFrom) reduced = true;
    if (reduced) return 'reduced';
    return gained ? 'gained' : 'none';
};

/** The on-screen toast for a change, or null for none. The bell notification says what exactly changed. */
export const accessChangeToast = (kind) => {
    if (kind === 'gained') return 'Your access was updated.';
    if (kind === 'reduced') return 'Your access was updated - some actions are no longer available.';
    return null;
};

/** Whether the effective permissions differ in anything a page would render differently. */
export const permissionsChanged = (before, after) => {
    if (!before || !after) return before !== after;
    if (before.version && after.version) return before.version !== after.version;
    return JSON.stringify(before) !== JSON.stringify(after);
};

/** Whether the user's org memberships (and their states) differ - an org suspension is one. */
export const orgsChanged = (before = [], after = []) => {
    const key = (orgs) => JSON.stringify([...(orgs || [])]
        .map(o => [String(o.orgId), String(o.orgState)])
        .sort((x, y) => x[0].localeCompare(y[0])));
    return key(before) !== key(after);
};

/**
 * Remembers signal ids for a while, so one server signal seen by two stream subscribers (the bell and the
 * notifications page both open one) refreshes once.
 */
export const createSignalDeduper = (windowMs = 10_000, now = () => Date.now()) => {
    const seen = new Map();
    return (id) => {
        const t = now();
        for (const [k, at] of seen) if (t - at > windowMs) seen.delete(k);
        if (!id) return true;
        if (seen.has(id)) return false;
        seen.set(id, t);
        return true;
    };
};
